import React, { useState } from 'react';
import { ethers } from 'ethers';

function GamePanel({ 
  contract, 
  usdtContract, 
  account, 
  timeLeft, 
  currentCycle,
  lastWinner,
  minInvestment,
  maxInvestment,
  userHeadInvestment,
  userTailInvestment,
  onRefresh,
  contractAddress,
  isRegistered
}) {
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCoinFlip, setShowCoinFlip] = useState(false);
  const [flipResult, setFlipResult] = useState(null);

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return '00:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const formatUSDT = (value) => {
    if (!value) return '0.00';
    return parseFloat(ethers.utils.formatUnits(value, 18)).toFixed(2);
  };

  const triggerCoinFlip = (room) => {
    setFlipResult(room);
    setShowCoinFlip(true);
    setTimeout(() => {
      setShowCoinFlip(false);
      setFlipResult(null);
    }, 2000);
  };

  const handleInvest = async () => {
    if (selectedRoom === null) {
      setError('Please select HEAD or TAIL');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const amountWei = ethers.utils.parseUnits(amount, 18);
    
    if (amountWei.lt(minInvestment)) {
      setError(`Minimum: ${formatUSDT(minInvestment)} USDT`);
      return;
    }
    if (amountWei.gt(maxInvestment)) {
      setError(`Maximum: ${formatUSDT(maxInvestment)} USDT`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const allowance = await usdtContract.allowance(account, contractAddress);
      
      if (allowance.lt(amountWei)) {
        const approveTx = await usdtContract.approve(
          contractAddress, 
          ethers.constants.MaxUint256
        );
        await approveTx.wait();
      }

      const tx = await contract.invest(amountWei, selectedRoom);
      await tx.wait();
      
      triggerCoinFlip(selectedRoom);
      setAmount('');
      setSelectedRoom(null);
      onRefresh();
    } catch (err) {
      console.error(err);
      if (err.code === 'ACTION_REJECTED' || err.code === 4001) {
        setError('Transaction rejected');
      } else if (err.reason) {
        setError(err.reason);
      } else {
        setError('Transaction failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const setMaxAmount = async () => {
    try {
      const balance = await usdtContract.balanceOf(account);
      const maxAllowed = balance.gt(maxInvestment) ? maxInvestment : balance;
      setAmount(ethers.utils.formatUnits(maxAllowed, 18));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="game-panel">
      {/* Coin Flip Animation Overlay */}
      {showCoinFlip && (
        <div className="coin-flip-overlay">
          <div className={`flip-coin ${flipResult === 0 ? 'flip-head' : 'flip-tail'}`}>
            <div className="flip-face flip-front">H</div>
            <div className="flip-face flip-back">T</div>
          </div>
          <p className="flip-text">Investment Placed!</p>
        </div>
      )}

      {/* Timer Section */}
      <div className="timer-section">
        <div className="cycle-badge">
          <span>Cycle</span>
          <strong>#{currentCycle?.toString() || '1'}</strong>
        </div>
        <div className="timer-display">
          <div className="timer-icon">⏱️</div>
          <div className="timer-value">{formatTime(timeLeft)}</div>
          <div className="timer-label">Until Next Round</div>
        </div>
        {lastWinner && lastWinner.cycle > 0 && (
          <div className="last-winner-badge">
            <span>Last Winner</span>
            <strong className={lastWinner.room === 0 ? 'head' : 'tail'}>
              {lastWinner.room === 0 ? '👑 HEAD' : '🎯 TAIL'}
            </strong>
          </div>
        )}
      </div>

      {/* Room Selection */}
      <div className="rooms-container">
        <div 
          className={`room-card head-room ${selectedRoom === 0 ? 'selected' : ''}`}
          onClick={() => account && isRegistered && setSelectedRoom(0)}
        >
          <div className="room-glow"></div>
          <div className="room-content">
            <div className="room-icon">👑</div>
            <h3>HEAD</h3>
            <div className="room-investment">
              <span className="label">Your Investment</span>
              <span className="amount">{formatUSDT(userHeadInvestment)} USDT</span>
            </div>
            {selectedRoom === 0 && <div className="selected-indicator">✓ Selected</div>}
          </div>
        </div>

        <div className="vs-badge">
          <div className="vs-inner">VS</div>
        </div>

        <div 
          className={`room-card tail-room ${selectedRoom === 1 ? 'selected' : ''}`}
          onClick={() => account && isRegistered && setSelectedRoom(1)}
        >
          <div className="room-glow"></div>
          <div className="room-content">
            <div className="room-icon">🎯</div>
            <h3>TAIL</h3>
            <div className="room-investment">
              <span className="label">Your Investment</span>
              <span className="amount">{formatUSDT(userTailInvestment)} USDT</span>
            </div>
            {selectedRoom === 1 && <div className="selected-indicator">✓ Selected</div>}
          </div>
        </div>
      </div>

      {/* Investment Form */}
      {account && isRegistered && (
        <div className="invest-form">
          <div className="input-wrapper">
            <input
              type="number"
              placeholder="Enter USDT amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={loading}
            />
            <button className="max-btn" onClick={setMaxAmount}>MAX</button>
          </div>
          
          <div className="limits-info">
            <span>Min: {formatUSDT(minInvestment)}</span>
            <span>Max: {formatUSDT(maxInvestment)}</span>
          </div>

          {error && <div className="error-msg">{error}</div>}

          <button 
            className={`btn btn-invest ${loading ? 'loading' : ''}`}
            onClick={handleInvest}
            disabled={loading || selectedRoom === null}
          >
            {loading ? (
              <><span className="spinner"></span> Processing...</>
            ) : (
              <>💰 Place Investment</>
            )}
          </button>

          <div className="game-rules">
            
            <div className="rule">💎 Winners get 2X returns</div>
          </div>
        </div>
      )}

      {account && !isRegistered && (
        <div className="register-prompt">
          <p>Register in My Account section to start playing</p>
        </div>
      )}

      {!account && (
        <div className="connect-prompt">
          <p>Connect wallet to start playing</p>
        </div>
      )}
    </div>
  );
}

export default GamePanel;
